import { useCallback, useEffect, useMemo } from 'react';

import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import useGetDoctorByUser from '@/hooks/doctor/useGetDoctorByUser';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { EAirbridgeLinkType } from '@/shared/types';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { useRouter } from 'next/router';

interface TelemedicineRequestCompleteQueries {
  diagnosisDate: string;
  medicalCategoryName: string;
  doctorId?: string;
  pid: string;
}

export interface TelemedicineRequestCompleteViewModel {
  diagnosisDate: string;
  medicalCategoryName: string;
  doctorName: string;
  doctorImage?: string;
  doctorId?: string;
  isLoading: boolean;
  pid?: string;
  handleClickAppButton: () => void;
  onHomeClick: () => void;
}

const useTelemedicineRequestCompleteViewModel = (): TelemedicineRequestCompleteViewModel => {
  const router = useRouter();
  const airbridgeDynamicLink = useAirbridgeDynamicLink('web_treatment_request_complete');

  const { diagnosisDate, medicalCategoryName, doctorId, pid } =
    router.query as Partial<TelemedicineRequestCompleteQueries>;

  const { doctorDetail, isDoctorDetailLoading } = useGetDoctorByUser({
    doctorId: doctorId || '',
    contactType: EDiagnosisContactType.NonF2F,
  });

  const doctorName = useMemo(() => {
    return doctorDetail?.doctor?.name || '';
  }, [doctorDetail]);

  const doctorImage = useMemo(() => {
    return doctorDetail?.doctor?.profileImageUrl;
  }, [doctorDetail]);

  const onHomeClick = useCallback(() => {
    router.push('/');
  }, [router]);

  const handleClickAppButton = useCallback(() => {
    airbridgeDynamicLink.navigateWithTracking(
      DeeplinkLoggerEventName.ClickCtaButton,
      {
        campaign: 'web_treatment_request_complete',
        term: '앱에서 진료 현황보기',
        ...(doctorId && { sub_id: doctorId }),
        ...(medicalCategoryName && { sub_id_1: medicalCategoryName }),
      },
      EAirbridgeLinkType.GENERAL
    );
  }, [doctorId, medicalCategoryName, airbridgeDynamicLink]);

  useEffect(() => {
    const handlePopState = () => {
      router.replace('/');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [router]);

  return {
    doctorId,
    onHomeClick,
    diagnosisDate: diagnosisDate || '',
    medicalCategoryName: medicalCategoryName || '',
    doctorName,
    doctorImage,
    isLoading: isDoctorDetailLoading || !router.isReady,
    pid,
    handleClickAppButton,
  };
};

export default useTelemedicineRequestCompleteViewModel;
